import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Alert,
  CircularProgress,
  Typography,
  TextField,
  Autocomplete,
} from '@mui/material';
import { Send } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { clearError, generateTask } from '../../store/strategistSlice';
import { searchTickets } from '../../store/ticketSlice';
import { toast } from 'react-toastify';
import OverflowTooltip from '../../shared/OverflowTooltip';

const TaskGeneratorCommandBar = ({ 
  projectId, 
  initialTicketId,
  initialTicketNumber,
  initialTicketTitle,
  isGenerating, 
  onTaskGenerated, 
  onClearRequested 
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { error, loading } = useSelector((state) => state.strategist);

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketOptions, setTicketOptions] = useState([]);
  const [suggestedTickets, setSuggestedTickets] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [loadingSuggested, setLoadingSuggested] = useState(false);
  const [prevProjectId, setPrevProjectId] = useState(projectId);

  const getTicketNumber = useCallback((option) => (
    option?.number || option?.ticket_number || option?.ticketNumber || ''
  ), []);

  const loadInitialTickets = useCallback(async () => {
    if (!projectId) return;

    setLoadingSuggested(true);
    try {
      const result = await dispatch(searchTickets({ projectId, searchTerm: '' })).unwrap();
      setSuggestedTickets(result.slice(0, 10));
    } catch (error) {
      setSuggestedTickets([]);
    } finally {
      setLoadingSuggested(false);
    }
  }, [dispatch, projectId]);

  // Expose clear function to parent
  React.useImperativeHandle(onClearRequested, () => ({
    clearFields: () => {
      setSelectedTicket(null);
      setSearchTerm('');
      setTicketOptions([]);
    }
  }));

  // Update state when props change
  useEffect(() => {
    if (initialTicketId && initialTicketNumber) {
      setSelectedTicket({
        id: initialTicketId,
        number: initialTicketNumber,
        ticket_number: initialTicketNumber,
        title: initialTicketTitle || '',
      });
    } else {
      setSelectedTicket(null);
    }
  }, [initialTicketId, initialTicketNumber, initialTicketTitle]);

  // Clear state when projectId changes
  useEffect(() => {
    if (projectId !== prevProjectId) {
      setSelectedTicket(null);
      setSearchTerm('');
      setTicketOptions([]);
      setSuggestedTickets([]);
      setPrevProjectId(projectId);
      loadInitialTickets();
    }
  }, [projectId, prevProjectId, loadInitialTickets]);

  // Load initial tickets on mount
  useEffect(() => {
    loadInitialTickets();
  }, [loadInitialTickets]);

  // Search tickets when search term changes
  useEffect(() => {
    const delayedSearch = setTimeout(async () => {
      if (!projectId) return;
      if (searchTerm.length >= 1) {
        setSearching(true);
        try {
          const result = await dispatch(searchTickets({ projectId, searchTerm })).unwrap();
          setTicketOptions(result.slice(0, 10));
        } catch (error) {
          console.error('Search tickets failed:', error);
          setTicketOptions([]);
        } finally {
          setSearching(false);
        }
      } else {
        setTicketOptions([]);
      }
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [dispatch, projectId, searchTerm]);

  const handleGenerate = async () => {
    if (!projectId || !(selectedTicket?.id || selectedTicket?.ticket_id)) {
      return;
    }
    
    try {
      await dispatch(generateTask({
        projectId,
        ticketId: selectedTicket.id || selectedTicket.ticket_id,
      })).unwrap();
      
      toast.info(t('agent.taskGenerator.generatingTasks', 'Generating tasks...'));
      
      if (onTaskGenerated) {
        await onTaskGenerated();
      }
    } catch (err) {
      toast.error(err || t('agent.taskGenerator.generationFailed', 'Task generation failed'));
    }
  };

  const hasTicket = initialTicketId && initialTicketNumber;
  const showGenerateButton = !hasTicket && !isGenerating;

  const ticketDisplayValue = React.useMemo(() => {
    const selectedValue = selectedTicket;
    const selectedNumber = getTicketNumber(selectedValue);
    return selectedNumber && selectedValue?.title
      ? `${selectedNumber} · ${selectedValue.title}`
      : selectedNumber || selectedValue?.title || '';
  }, [getTicketNumber, selectedTicket]);

  return (
    <Box className="agent-command-bar-container">
      <Box className="command-bar-header">
        <Typography variant="h6" className="command-bar-title">
          {hasTicket ? t('agent.taskGenerator.selectedTicket', 'Selected Ticket') : t('agent.taskGenerator.selectTicket', 'Select Ticket')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" className="command-bar-error" onClose={() => dispatch(clearError())}>
          {error.message || error}
        </Alert>
      )}

      {hasTicket ? (
        <Box className="command-bar-requirement-display">
          <Typography variant="body1" className="requirement-text">
            <strong>{initialTicketNumber}</strong> - {initialTicketTitle}
          </Typography>
        </Box>
      ) : (
        <Box className="command-bar-input-container">
          <Autocomplete
            options={searchTerm.length >= 1 ? ticketOptions : suggestedTickets}
            value={selectedTicket}
            onChange={(event, newValue) => {
              setSelectedTicket(newValue);
              setSearchTerm('');
            }}
            onInputChange={(event, newInputValue, reason) => {
              if (reason !== 'reset' && reason !== 'clear') {
                setSearchTerm(newInputValue);
              }
            }}
            filterOptions={(x) => x}
            loading={searching || loadingSuggested}
            clearOnEscape
            disableClearable={false}
            disabled={loading.generateTask}
            getOptionLabel={(option) => {
              if (!option) return '';
              if (typeof option === 'string') return option;
              const optionNumber = getTicketNumber(option);
              return optionNumber && option?.title
                ? `${optionNumber} · ${option.title}`
                : optionNumber || option?.title || '';
            }}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 0, flexShrink: 0 }}>
                    {getTicketNumber(option)}
                  </Typography>
                  <OverflowTooltip
                    title={option.title}
                    tooltipWidth={400}
                    variant="body2"
                    maxLines={1}
                    sx={{ minWidth: 0, flex: 1 }}
                  >
                    {option.title}
                  </OverflowTooltip>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <OverflowTooltip
                title={ticketDisplayValue}
                tooltipWidth={400}
                placement="top-start"
                component="div"
                sx={{ width: '100%' }}
              >
                <TextField
                  {...params}
                  placeholder={t('agent.taskGenerator.ticketPlaceholder', 'Search for a ticket...')}
                  InputProps={{
                    ...params.InputProps,
                    sx: {
                      '& input': {
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap'
                      }
                    },
                    endAdornment: (
                      <>
                        {searching || loadingSuggested ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              </OverflowTooltip>
            )}
            noOptionsText={searchTerm.length >= 1 ? t('agent.taskGenerator.noTicketsFound', 'No tickets found') : (loadingSuggested ? '' : t('agent.taskGenerator.noSuggestedTickets', 'No suggested tickets'))}
            fullWidth
          />
        </Box>
      )}

      <Box className="command-bar-actions">
        {showGenerateButton && (
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={!selectedTicket || !projectId || loading.generateTask}
            startIcon={loading.generateTask ? <CircularProgress size={20} /> : <Send />}
            className="command-bar-process-button"
          >
            {loading.generateTask ? t('agent.taskGenerator.generating', 'Generating...') : t('agent.taskGenerator.generate', 'Generate Tasks')}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default TaskGeneratorCommandBar;
